/**
 * Message composer dialog for sending a task message to a driver's feed.
 *
 * The recommended message is shown as ghost text (not real input): typing
 * overwrites it, Tab accepts it into the box. This preserves the suggestion
 * while keeping the dispatcher in control of the final wording.
 */

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";

import { accent, text, zinc } from "../colors";
import { ActionButton } from "./primitives";

interface MessageComposerProps {
  driverName: string;
  /** Recommended message shown as ghost text and accepted on Tab. */
  recommendation?: string;
  sending?: boolean;
  onCancel: () => void;
  /** `resolve` distinguishes "Send & resolve" (close the task) from "Send" (await driver response). */
  onSend: (text: string, resolve: boolean) => void;
}

export function MessageComposer({ driverName, recommendation, sending, onCancel, onSend }: MessageComposerProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, []);

  const showGhost = value === "" && !!recommendation;

  const submit = (resolve: boolean) => {
    const finalText = value.trim() ? value : (recommendation ?? "");
    if (!finalText.trim() || sending) return;
    onSend(finalText, resolve);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Tab" && showGhost) {
      e.preventDefault();
      setValue(recommendation ?? "");
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: zinc[900],
            border: `1px solid ${zinc[700]}`,
            borderRadius: "14px",
            backgroundImage: "none",
          },
        },
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: text.primary, mb: 0.25 }}>
          Message {driverName}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: text.muted, mb: 1.75 }}>
          Sends to the driver's feed as a text-to-speech message.
        </Typography>

        <Box
          sx={{
            position: "relative",
            bgcolor: zinc[950],
            border: `1px solid ${zinc[700]}`,
            borderRadius: "8px",
            "&:focus-within": { borderColor: accent.blue },
          }}
        >
          {showGhost && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                p: 1.5,
                fontSize: 14,
                lineHeight: 1.5,
                fontFamily: "inherit",
                color: text.muted,
                whiteSpace: "pre-wrap",
                pointerEvents: "none",
              }}
            >
              {recommendation}
            </Box>
          )}
          <Box
            component="textarea"
            ref={inputRef}
            value={value}
            rows={5}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              position: "relative",
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              resize: "none",
              bgcolor: "transparent",
              color: text.primary,
              border: "none",
              outline: "none",
              p: 1.5,
              fontSize: 14,
              lineHeight: 1.5,
              fontFamily: "inherit",
              caretColor: accent.blue,
            }}
          />
        </Box>

        <Typography sx={{ fontSize: 12, color: text.muted, mt: 1 }}>
          {recommendation ? (
            <>
              <Box component="span" sx={{ color: text.secondary, fontWeight: 600 }}>
                Tab
              </Box>{" "}
              to accept the suggestion ·{" "}
            </>
          ) : null}
          <Box component="span" sx={{ color: text.secondary, fontWeight: 600 }}>
            Enter
          </Box>{" "}
          to send ·{" "}
          <Box component="span" sx={{ color: text.secondary, fontWeight: 600 }}>
            Shift+Enter
          </Box>{" "}
          for a new line
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <ActionButton label="Cancel" variant="ghost" onClick={onCancel} />
          <ActionButton label={sending ? "Sending…" : "Send"} variant="ghost" onClick={() => submit(false)} />
          <ActionButton label={sending ? "Sending…" : "Send & resolve"} onClick={() => submit(true)} />
        </Box>
      </Box>
    </Dialog>
  );
}
